// TwoFactorSetupPage — 2FA setup wizard
// Steps: 1) Install app → 2) Scan QR → 3) Verify code → 4) Save backup codes

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PartnerLogo } from '../../components/Brand';

const STEPS = [
  { key: 'install',   label: 'Install' },
  { key: 'scan',      label: 'Scan'    },
  { key: 'verify',    label: 'Verify'  },
  { key: 'backup',    label: 'Backup'  },
] as const;
type StepKey = typeof STEPS[number]['key'];

const BACKUP_CODES = [
  'A4F2-9C18', 'B72E-3D5A', 'F08C-1B9D', 'E32A-7F4E',
  'C8D5-2A6F', '9B1E-4C7A', '7F3D-6B82', '5E9A-1D3C',
];

export function TwoFactorSetupPage() {
  const nav = useNavigate();
  const [step, setStep] = useState<StepKey>('install');
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [saved, setSaved] = useState(false);
  const idx = STEPS.findIndex((s) => s.key === step);

  function setCodeAt(i: number, v: string) {
    if (v.length > 1) return;
    setCode((curr) => { const n = [...curr]; n[i] = v; return n; });
  }

  function next() {
    if (idx < STEPS.length - 1) setStep(STEPS[idx + 1].key);
    else { setSaved(true); setTimeout(() => nav('/portal/security'), 2000); }
  }

  return (
    <div className="min-h-screen bg-tf-surface-1 flex flex-col">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl">
        <button onClick={() => idx === 0 ? nav(-1) : setStep(STEPS[idx - 1].key)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">2FA setup</div>
        <button onClick={() => nav(-1)} className="px-3 text-[15px] text-tf-blue">Cancel</button>
      </div>

      <div className="px-4 pt-3 flex items-center gap-1" data-testid="2fa-progress">
        {STEPS.map((s, i) => (
          <div key={s.key} className={['h-1 flex-1 rounded-full', i <= idx ? 'bg-tf-blue' : 'bg-[#E5E5EA]'].join(' ')} />
        ))}
      </div>

      <div className="flex-1 overflow-y-auto px-4 pt-6 pb-32">
        {saved && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/90 backdrop-blur-md">
            <div className="text-center">
              <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-[#34C759] text-white">
                <svg viewBox="0 0 24 24" className="h-12 w-12" fill="none" stroke="currentColor" strokeWidth="3"><path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </div>
              <div className="mt-3 text-[20px] font-bold text-tf-text-1">2FA enabled</div>
            </div>
          </div>
        )}

        {step === 'install' && (
          <div>
            <div className="text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-[20px] bg-tf-blue/15 text-[36px]">🔐</div>
              <h2 className="mt-3 text-[22px] font-bold tracking-tight text-tf-text-1">Install an authenticator app</h2>
              <p className="mt-1 text-[14px] text-tf-text-3">You'll need an app that generates one-time codes. We recommend:</p>
            </div>
            <div className="mt-6 space-y-2">
              {[
                { name: 'Google Authenticator', sub: 'Free · iOS + Android', emoji: '🔵' },
                { name: 'Authy',                sub: 'Free · Cloud backup',  emoji: '🟣' },
                { name: '1Password',            sub: 'Paid · Built-in',      emoji: '🟢' },
                { name: 'Microsoft Authenticator', sub: 'Free · iOS + Android', emoji: '🔷' },
              ].map((app, i) => (
                <div key={app.name} className="flex items-center gap-3 rounded-[14px] border border-tf-border bg-white p-3" data-testid={`auth-app-${i}`}>
                  <div className="flex h-10 w-10 items-center justify-center rounded-[10px] bg-tf-surface-1 text-[18px]">{app.emoji}</div>
                  <div className="flex-1">
                    <div className="text-[14px] font-semibold text-tf-text-1">{app.name}</div>
                    <div className="text-[11px] text-tf-text-3">{app.sub}</div>
                  </div>
                  <button onClick={() => alert('Open App Store')} className="text-[12px] font-semibold text-tf-blue">Get</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {step === 'scan' && (
          <div>
            <h2 className="text-[22px] font-bold tracking-tight text-tf-text-1">Scan this QR code</h2>
            <p className="mt-1 mb-4 text-[14px] text-tf-text-3">Open your authenticator app and scan the QR code below.</p>
            <div className="flex flex-col items-center">
              <div className="rounded-[20px] bg-white p-5 shadow-md">
                {/* SVG QR placeholder */}
                <svg viewBox="0 0 160 160" className="h-48 w-48">
                  {(() => {
                    const cells: React.ReactElement[] = [];
                    const n = 20;
                    const cells2d: boolean[][] = [];
                    for (let r = 0; r < n; r++) {
                      cells2d.push([]);
                      for (let c = 0; c < n; c++) {
                        cells2d[r].push(((r * 7 + c * 13) ^ (r << c)) % 3 === 0);
                      }
                    }
                    // Position markers
                    for (const [rr, cc] of [[0, 0], [0, n - 7], [n - 7, 0]]) {
                      for (let dr = 0; dr < 7; dr++) for (let dc = 0; dc < 7; dc++) {
                        const isOuter = dr === 0 || dr === 6 || dc === 0 || dc === 6;
                        const isInner = dr >= 2 && dr <= 4 && dc >= 2 && dc <= 4;
                        if (isOuter || isInner) cells2d[rr + dr][cc + dc] = true;
                      }
                    }
                    for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) {
                      if (cells2d[r][c]) cells.push(<rect key={`${r}-${c}`} x={c * 8} y={r * 8} width="8" height="8" fill="#1C1C1E" />);
                    }
                    return cells;
                  })()}
                </svg>
              </div>
              <div className="mt-4 max-w-xs text-center">
                <div className="text-[11px] uppercase tracking-wider text-tf-text-3">Or enter this key manually</div>
                <div className="mt-1 rounded-[10px] border border-tf-border bg-white p-2 font-mono text-[12px] text-tf-text-1" data-testid="manual-key">JBSWY3DPEHPK3PXP FUNDER PRO LIVE</div>
                <button onClick={() => { navigator.clipboard?.writeText('JBSWY3DPEHPK3PXP'); alert('Copied!'); }} className="mt-1 text-[11px] font-semibold text-tf-blue">Copy key</button>
              </div>
            </div>
          </div>
        )}

        {step === 'verify' && (
          <div>
            <h2 className="text-[22px] font-bold tracking-tight text-tf-text-1">Enter the 6-digit code</h2>
            <p className="mt-1 mb-5 text-[14px] text-tf-text-3">Open your authenticator app and enter the code it shows.</p>
            <div className="flex items-center justify-center gap-1.5" data-testid="code-input">
              {code.map((c, i) => (
                <input
                  key={i}
                  value={c}
                  onChange={(e) => setCodeAt(i, e.target.value)}
                  maxLength={1}
                  data-testid={`code-${i}`}
                  className="h-14 w-12 rounded-[12px] border-2 border-tf-border bg-white text-center text-[24px] font-bold text-tf-text-1 focus:outline-none focus:border-tf-blue tabular-nums"
                />
              ))}
            </div>
            <p className="mt-3 text-center text-[11px] text-tf-text-3">Code refreshes every 30 seconds</p>
          </div>
        )}

        {step === 'backup' && (
          <div>
            <h2 className="text-[22px] font-bold tracking-tight text-tf-text-1">Save your backup codes</h2>
            <p className="mt-1 mb-4 text-[14px] text-tf-text-3">If you lose your authenticator, you'll need these one-time codes to sign in. Keep them somewhere safe.</p>
            <div className="rounded-[16px] border border-[#FF9500]/30 bg-[#FF9500]/[0.04] p-4">
              <div className="grid grid-cols-2 gap-2 font-mono text-[14px] font-semibold text-tf-text-1" data-testid="backup-codes">
                {BACKUP_CODES.map((c) => (
                  <div key={c} className="rounded-[8px] bg-white p-2 text-center">{c}</div>
                ))}
              </div>
            </div>
            <div className="mt-3 flex gap-2">
              <button onClick={() => { navigator.clipboard?.writeText(BACKUP_CODES.join('\n')); alert('Copied!'); }} className="flex-1 h-10 rounded-full bg-tf-surface-1 text-[13px] font-semibold text-tf-text-1 active:opacity-40">Copy</button>
              <button onClick={() => alert('Download as .txt')} className="flex-1 h-10 rounded-full bg-tf-surface-1 text-[13px] font-semibold text-tf-text-1 active:opacity-40">Download .txt</button>
            </div>
            <p className="mt-3 text-center text-[11px] text-tf-warning">⚠ These codes are only shown once</p>
          </div>
        )}
      </div>

      <div className="fixed inset-x-0 bottom-0 z-10 bg-white/80 backdrop-blur-xl border-t border-tf-border px-4 py-3" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
        <button onClick={next} data-testid="2fa-next" className="flex h-12 w-full items-center justify-center rounded-full bg-tf-text-1 text-[15px] font-semibold text-white active:scale-[0.98]">
          {step === 'install' ? 'I have an app' : step === 'scan' ? 'I scanned the QR' : step === 'verify' ? 'Verify code' : 'I saved my codes'}
        </button>
      </div>
    </div>
  );
}
